import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import {
  Ticket, Plus, Save, Trash2, X, Edit2, CheckCircle, AlertCircle, RefreshCw,
  Users, Download, Check, Image, BarChart3, Lock, Unlock, Inbox, ThumbsUp, ThumbsDown, ShieldCheck, Repeat, MessageCircle,
} from 'lucide-react';
import MediaPicker from '../components/MediaPicker';
import OfferAnalyticsReport from '../components/OfferAnalyticsReport';
import OfferDescription from '../components/OfferDescription';

interface Offer {
  id: number;
  key: string;
  region: 'local' | 'mainland';
  restaurantName: string;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  redeemInstructions?: string | null;
  validUntil?: number | null;
  maxClaims?: number | null;
  isActive: number;
  sortOrder: number;
  merchantVerifyCode?: string | null;
  contactPhone?: string | null;
  allowReclaim?: number | boolean | null;
  claimsCount?: number;
}

interface OfferClaim {
  id: number;
  offerId?: number | null;
  offerKey?: string | null;
  restaurantName?: string | null;
  offerTitle?: string | null;
  offerRegion?: string | null;
  phone: string;
  name?: string | null;
  redeemCode?: string | null;
  status: 'claimed' | 'redeemed' | 'expired';
  claimedAt: number;
  redeemedAt?: number | null;
  redeemAttempts?: number;
  lockedAt?: number | null;
}

interface MerchantSubmission {
  id: number;
  key?: string | null;
  region: 'local' | 'mainland';
  restaurantName: string;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  redeemInstructions?: string | null;
  validUntil?: number | null;
  maxClaims?: number | null;
  merchantVerifyCode?: string | null;
  status: 'pending' | 'approved' | 'rejected';
  submitterName?: string | null;
  submitterPhone?: string | null;
  submitterEmail?: string | null;
  adminNotes?: string | null;
  offerId?: number | null;
  createdAt: number;
}

const CLAIM_STATUS_LABELS: Record<string, string> = {
  claimed: '已領取',
  redeemed: '已兌換',
  expired: '已過期',
};

const CLAIM_STATUS_COLORS: Record<string, string> = {
  claimed: 'bg-amber-100 text-amber-700',
  redeemed: 'bg-green-100 text-green-700',
  expired: 'bg-gray-100 text-gray-600',
};

const SUBMISSION_STATUS_LABELS: Record<string, string> = {
  pending: '待審批',
  approved: '已上架',
  rejected: '已拒絕',
};

const SUBMISSION_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-gray-100 text-gray-600',
};

function randomVerifyCode(): string {
  return String(Math.floor(1000 + Math.random() * 9000));
}

const EMPTY_FORM: Partial<Offer> = {
  key: '',
  region: 'local',
  restaurantName: '',
  title: '',
  description: '',
  imageUrl: '',
  redeemInstructions: '',
  validUntil: null,
  maxClaims: null,
  sortOrder: 0,
  isActive: 1,
  merchantVerifyCode: '1493',
  contactPhone: '',
  allowReclaim: 0,
};

export default function AdminOffers() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'offers' | 'claims' | 'submissions' | 'analytics'>('offers');
  const [offers, setOffers] = useState<Offer[]>([]);
  const [claims, setClaims] = useState<OfferClaim[]>([]);
  const [submissions, setSubmissions] = useState<MerchantSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [form, setForm] = useState<Partial<Offer>>(EMPTY_FORM);
  const [validUntilDate, setValidUntilDate] = useState('');
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const [claimOfferFilter, setClaimOfferFilter] = useState('0');
  const [claimStatusFilter, setClaimStatusFilter] = useState('');
  const [submissionStatusFilter, setSubmissionStatusFilter] = useState('pending');

  const showToast = (msg: string, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(null), 4000); }
    else { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); }
  };

  const fetchOffers = async () => {
    try {
      const res = await apiFetch('/api/public/admin/offers');
      if (!res.ok) throw new Error('載入失敗');
      const data = await res.json();
      setOffers(Array.isArray(data) ? data : data.data || []);
    } catch (e: any) {
      setError(e.message || '載入失敗');
    }
  };

  const fetchClaims = async () => {
    try {
      const params = new URLSearchParams();
      if (claimOfferFilter !== '0') params.set('offerId', claimOfferFilter);
      if (claimStatusFilter) params.set('status', claimStatusFilter);
      const res = await apiFetch(`/api/public/admin/offer-claims?${params.toString()}`);
      if (!res.ok) throw new Error('載入失敗');
      const data = await res.json();
      setClaims(Array.isArray(data) ? data : data.data || []);
    } catch (e: any) {
      setError(e.message || '載入失敗');
    }
  };

  const fetchSubmissions = async () => {
    try {
      const params = new URLSearchParams();
      if (submissionStatusFilter) params.set('status', submissionStatusFilter);
      const res = await apiFetch(`/api/public/admin/merchant-offer-submissions?${params.toString()}`);
      if (!res.ok) throw new Error('載入失敗');
      const data = await res.json();
      setSubmissions(Array.isArray(data) ? data : data.data || []);
    } catch (e: any) {
      setError(e.message || '載入失敗');
    }
  };

  const reload = async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchOffers(), fetchClaims(), fetchSubmissions()]);
    setLoading(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    reload();
  }, [navigate]);

  useEffect(() => {
    if (tab === 'claims') fetchClaims();
  }, [claimOfferFilter, claimStatusFilter, tab]);

  useEffect(() => {
    if (tab === 'submissions') fetchSubmissions();
  }, [submissionStatusFilter, tab]);

  // ---------- Offer CRUD ----------

  const openCreate = () => {
    setEditingOffer(null);
    setForm(EMPTY_FORM);
    setValidUntilDate('');
    setModalOpen(true);
  };

  const openEdit = (offer: Offer) => {
    setEditingOffer(offer);
    setForm({ ...offer });
    setValidUntilDate(
      offer.validUntil ? new Date(offer.validUntil * 1000).toISOString().slice(0, 10) : ''
    );
    setModalOpen(true);
  };

  const saveOffer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.key?.trim() || !form.restaurantName?.trim() || !form.title?.trim()) {
      showToast('請填寫 Key、餐廳名稱與優惠標題', true); return;
    }
    if (!/^[a-z0-9_-]+$/.test(form.key.trim())) {
      showToast('Key 只可使用小寫英文、數字、底線或橫線', true); return;
    }
    const verifyCode = (form.merchantVerifyCode || '').trim();
    if (verifyCode && !/^\d{4}$/.test(verifyCode)) {
      showToast('商家驗證碼必須為 4 位數字', true); return;
    }
    const payload = {
      ...form,
      merchantVerifyCode: verifyCode || null,
      contactPhone: (form.contactPhone || '').replace(/\D/g, '') || null,
      allowReclaim: form.allowReclaim ? 1 : 0,
      validUntil: validUntilDate ? Math.floor(new Date(`${validUntilDate}T23:59:59`).getTime() / 1000) : null,
      maxClaims: form.maxClaims ? Number(form.maxClaims) : null,
      sortOrder: Number(form.sortOrder || 0),
    };
    try {
      const url = editingOffer
        ? `/api/public/admin/offers/${editingOffer.id}`
        : '/api/public/admin/offers';
      const res = await apiFetch(url, {
        method: editingOffer ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      showToast(editingOffer ? '已更新優惠' : '已建立優惠');
      setModalOpen(false);
      fetchOffers();
    } catch (err: any) { showToast(err.message || '儲存失敗', true); }
  };

  const deleteOffer = async (offer: Offer) => {
    if (!confirm(`確定刪除「${offer.restaurantName} ${offer.title}」？索取記錄會保留。`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/offers/${offer.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('刪除失敗');
      showToast('已刪除');
      fetchOffers();
    } catch (err: any) { showToast(err.message || '刪除失敗', true); }
  };

  const toggleActive = async (offer: Offer) => {
    try {
      const res = await apiFetch(`/api/public/admin/offers/${offer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !offer.isActive }),
      });
      if (!res.ok) throw new Error('更新失敗');
      fetchOffers();
    } catch (err: any) { showToast(err.message || '更新失敗', true); }
  };

  // ---------- Claims ----------

  const markRedeemed = async (claim: OfferClaim) => {
    if (!confirm(`確定將兌換碼 ${claim.redeemCode} 標記為「已兌換」？\n顧客會收到 WhatsApp 兌換確認訊息。`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/offer-claims/${claim.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'redeemed' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '更新失敗');
      showToast('已標記為已兌換');
      fetchClaims();
      fetchOffers();
    } catch (err: any) { showToast(err.message || '更新失敗', true); }
  };

  const unlockClaim = async (claim: OfferClaim) => {
    if (!confirm(`確定解鎖兌換碼 ${claim.redeemCode}？錯誤嘗試次數會歸零。`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/offer-claims/${claim.id}/unlock`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '解鎖失敗');
      showToast('已解鎖');
      fetchClaims();
    } catch (err: any) { showToast(err.message || '解鎖失敗', true); }
  };

  // ---------- Merchant submissions ----------

  const approveSubmission = async (sub: MerchantSubmission) => {
    if (!confirm(`確定批准「${sub.restaurantName} ${sub.title}」？\n批准後會即時上架到 /offers 頁面。`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/merchant-offer-submissions/${sub.id}/approve`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '批准失敗');
      showToast(`已上架（key: ${data.data?.key || data.key || '自動產生'}）`);
      fetchSubmissions();
      fetchOffers();
    } catch (err: any) { showToast(err.message || '批准失敗', true); }
  };

  const rejectSubmission = async (sub: MerchantSubmission) => {
    const notes = prompt(`拒絕「${sub.restaurantName} ${sub.title}」的原因（可留空）：`) ?? '';
    if (notes === null) return;
    try {
      const res = await apiFetch(`/api/public/admin/merchant-offer-submissions/${sub.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminNotes: notes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '拒絕失敗');
      showToast('已拒絕');
      fetchSubmissions();
    } catch (err: any) { showToast(err.message || '拒絕失敗', true); }
  };

  const exportCsv = () => {
    const header = ['兌換碼', '地區', '餐廳', '優惠', '電話', '姓名', '狀態', '領取時間', '兌換時間', '核銷嘗試', '鎖定'];
    const rows = claims.map((c) => [
      c.redeemCode || '',
      c.offerRegion === 'mainland' ? '內地' : '香港',
      c.restaurantName || '',
      c.offerTitle || '',
      c.phone,
      c.name || '',
      CLAIM_STATUS_LABELS[c.status] || c.status,
      new Date(c.claimedAt * 1000).toLocaleString('zh-HK'),
      c.redeemedAt ? new Date(c.redeemedAt * 1000).toLocaleString('zh-HK') : '',
      String(c.redeemAttempts || 0),
      c.lockedAt ? '已鎖定' : '',
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `offer-claims-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDateTime = (ts?: number | null) =>
    ts ? new Date(ts * 1000).toLocaleString('zh-HK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const formatDate = (ts?: number | null) =>
    ts ? new Date(ts * 1000).toLocaleDateString('zh-HK') : '長期有效';

  const offerOptions = useMemo(() => offers.map((o) => ({ id: o.id, label: `${o.restaurantName} · ${o.title}` })), [offers]);

  return (
    <AdminLayout currentPage="offers">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Ticket className="w-6 h-6 text-red-600" />
          食肆優惠
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          管理 /offers 頁展示的餐廳優惠。顧客經 WhatsApp 領取後會自動記錄電話、派發兌換碼並成為辣谷之友。
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span className="flex items-center gap-2"><AlertCircle size={16} /> {error}</span>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-2"><CheckCircle size={16} /> {success}</span>
          <button onClick={() => setSuccess(null)}><X size={18} /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-gray-200 dark:border-gray-700">
        {([
          { id: 'offers', label: '優惠管理', icon: Ticket },
          { id: 'claims', label: '索取記錄', icon: Users },
          { id: 'submissions', label: '商家提交', icon: Inbox },
          { id: 'analytics', label: '成效報表', icon: BarChart3 },
        ] as const).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.id
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'offers' && (
        <>
          <div className="mb-4 flex justify-end gap-2">
            <button onClick={reload} className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
              <RefreshCw size={16} /> 重新整理
            </button>
            <button onClick={openCreate} className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors">
              <Plus size={16} /> 新增優惠
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中...</div>
          ) : offers.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              尚未建立優惠
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Key</th>
                    <th className="px-4 py-3 text-left font-medium">地區</th>
                    <th className="px-4 py-3 text-left font-medium">餐廳 / 優惠</th>
                    <th className="px-4 py-3 text-left font-medium">名額</th>
                    <th className="px-4 py-3 text-left font-medium">有效期</th>
                    <th className="px-4 py-3 text-left font-medium">自助核銷</th>
                    <th className="px-4 py-3 text-left font-medium">狀態</th>
                    <th className="px-4 py-3 text-right font-medium">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {offers.map((offer) => (
                    <tr key={offer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">{offer.key}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${offer.region === 'mainland' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                          {offer.region === 'mainland' ? '內地' : '香港'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{offer.restaurantName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{offer.title}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {offer.maxClaims ? `${offer.claimsCount || 0} / ${offer.maxClaims}` : `${offer.claimsCount || 0}（不限）`}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatDate(offer.validUntil)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {offer.merchantVerifyCode ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" title="顧客出示 QR，店員輸入此 4 位碼核銷">
                            <ShieldCheck size={12} /> {offer.merchantVerifyCode}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400" title="未啟用 QR 自助核銷">—</span>
                        )}
                        {!!offer.allowReclaim && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300 ml-1" title="兌換後可再次索取新兌換碼">
                            <Repeat size={12} /> 可重領
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleActive(offer)} className={`text-xs px-2 py-1 rounded ${offer.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {offer.isActive ? '上架' : '下架'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openEdit(offer)} className="text-blue-600 hover:text-blue-700" title="編輯"><Edit2 size={16} /></button>
                          <button onClick={() => deleteOffer(offer)} className="text-red-600 hover:text-red-700" title="刪除"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'claims' && (
        <>
          <div className="mb-4 flex flex-col sm:flex-row justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={claimOfferFilter}
                onChange={(e) => setClaimOfferFilter(e.target.value)}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white"
              >
                <option value="0">全部優惠</option>
                {offerOptions.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
              <select
                value={claimStatusFilter}
                onChange={(e) => setClaimStatusFilter(e.target.value)}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white"
              >
                <option value="">全部狀態</option>
                <option value="claimed">已領取</option>
                <option value="redeemed">已兌換</option>
                <option value="expired">已過期</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={fetchClaims} className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
                <RefreshCw size={16} /> 重新整理
              </button>
              <button onClick={exportCsv} disabled={claims.length === 0} className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors disabled:opacity-50">
                <Download size={16} /> 匯出 CSV
              </button>
            </div>
          </div>

          {claims.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              沒有符合條件的索取記錄
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">兌換碼</th>
                    <th className="px-4 py-3 text-left font-medium">餐廳 / 優惠</th>
                    <th className="px-4 py-3 text-left font-medium">電話</th>
                    <th className="px-4 py-3 text-left font-medium">姓名</th>
                    <th className="px-4 py-3 text-left font-medium">領取時間</th>
                    <th className="px-4 py-3 text-left font-medium">狀態</th>
                    <th className="px-4 py-3 text-left font-medium">核銷嘗試</th>
                    <th className="px-4 py-3 text-right font-medium">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {claims.map((claim) => (
                    <tr key={claim.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 font-mono text-xs font-bold text-gray-900 dark:text-white">{claim.redeemCode}</td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900 dark:text-white">{claim.restaurantName || claim.offerKey || '-'}</div>
                        {claim.offerTitle && <div className="text-xs text-gray-500 dark:text-gray-400">{claim.offerTitle}</div>}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{claim.phone}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{claim.name || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatDateTime(claim.claimedAt)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${CLAIM_STATUS_COLORS[claim.status] || 'bg-gray-100 text-gray-600'}`}>
                          {CLAIM_STATUS_LABELS[claim.status] || claim.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {claim.lockedAt ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                            <Lock size={12} /> 已鎖定
                          </span>
                        ) : (claim.redeemAttempts || 0) > 0 ? (
                          <span className="text-xs text-amber-600 dark:text-amber-400">錯 {claim.redeemAttempts} 次</span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {claim.status === 'claimed' && (
                            <button
                              onClick={() => markRedeemed(claim)}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200"
                              title="標記已兌換"
                            >
                              <Check size={14} /> 已兌換
                            </button>
                          )}
                          {claim.lockedAt && (
                            <button
                              onClick={() => unlockClaim(claim)}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/40 dark:text-purple-300"
                              title="解除鎖定（錯誤次數歸零）"
                            >
                              <Unlock size={14} /> 解鎖
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'submissions' && (
        <>
          <div className="mb-4 flex flex-col sm:flex-row justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={submissionStatusFilter}
                onChange={(e) => setSubmissionStatusFilter(e.target.value)}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white"
              >
                <option value="pending">待審批</option>
                <option value="approved">已上架</option>
                <option value="rejected">已拒絕</option>
                <option value="">全部</option>
              </select>
              <p className="text-xs text-gray-400">商家經 /merchant/submit-offer 提交的優惠，批准後即時上架。</p>
            </div>
            <button onClick={fetchSubmissions} className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
              <RefreshCw size={16} /> 重新整理
            </button>
          </div>

          {submissions.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              沒有符合條件的商家提交
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">餐廳 / 優惠</th>
                    <th className="px-4 py-3 text-left font-medium">地區</th>
                    <th className="px-4 py-3 text-left font-medium">驗證碼</th>
                    <th className="px-4 py-3 text-left font-medium">有效期 / 名額</th>
                    <th className="px-4 py-3 text-left font-medium">聯絡人</th>
                    <th className="px-4 py-3 text-left font-medium">提交時間</th>
                    <th className="px-4 py-3 text-left font-medium">狀態</th>
                    <th className="px-4 py-3 text-right font-medium">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {submissions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 align-top">
                      <td className="px-4 py-3 max-w-xs">
                        <div className="font-medium text-gray-900 dark:text-white">{sub.restaurantName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{sub.title}</div>
                        {sub.description && (
                          <details className="mt-1">
                            <summary className="text-xs text-blue-600 cursor-pointer">查看細則</summary>
                            <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line mt-1">{sub.description}</p>
                          </details>
                        )}
                        {sub.imageUrl && (
                          <a href={sub.imageUrl} target="_blank" rel="noreferrer" className="inline-block mt-1">
                            <img src={sub.imageUrl} alt="優惠圖" className="w-16 h-12 object-cover rounded border border-gray-200 dark:border-gray-600" />
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${sub.region === 'mainland' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                          {sub.region === 'mainland' ? '內地' : '香港'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {sub.merchantVerifyCode ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                            <ShieldCheck size={12} /> {sub.merchantVerifyCode}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap text-xs">
                        <div>{formatDate(sub.validUntil)}</div>
                        <div className="text-gray-400">{sub.maxClaims ? `名額 ${sub.maxClaims}` : '不限名額'}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">
                        <div>{sub.submitterName || '-'}</div>
                        <div className="text-gray-400">{sub.submitterPhone || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatDateTime(sub.createdAt)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${SUBMISSION_STATUS_COLORS[sub.status] || 'bg-gray-100 text-gray-600'}`}>
                          {SUBMISSION_STATUS_LABELS[sub.status] || sub.status}
                        </span>
                        {sub.status === 'approved' && sub.key && (
                          <div className="text-xs text-gray-400 mt-1 font-mono">{sub.key}</div>
                        )}
                        {sub.status === 'rejected' && sub.adminNotes && (
                          <div className="text-xs text-gray-400 mt-1 max-w-[120px] truncate" title={sub.adminNotes}>{sub.adminNotes}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {sub.status === 'pending' && (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => approveSubmission(sub)}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200"
                              title="批准並上架"
                            >
                              <ThumbsUp size={14} /> 批准上架
                            </button>
                            <button
                              onClick={() => rejectSubmission(sub)}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
                              title="拒絕"
                            >
                              <ThumbsDown size={14} /> 拒絕
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'analytics' && (
        <OfferAnalyticsReport offers={offers} />
      )}

      {/* Offer create/edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={saveOffer} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{editingOffer ? '編輯優惠' : '新增優惠'}</h2>
                <button type="button" onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key *</label>
                  <input
                    disabled={!!editingOffer}
                    value={form.key || ''}
                    onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase() })}
                    placeholder="例如 chan-kei-10off"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white disabled:bg-gray-100"
                  />
                  <p className="text-xs text-gray-400 mt-1">WhatsApp 關鍵字 OFFER:{form.key || 'xxx'}，建立後不可修改。</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">地區 *</label>
                  <select
                    value={form.region || 'local'}
                    onChange={(e) => setForm({ ...form, region: e.target.value as 'local' | 'mainland' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                  >
                    <option value="local">香港本地</option>
                    <option value="mainland">內地</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">餐廳名稱 *</label>
                  <input
                    value={form.restaurantName || ''}
                    onChange={(e) => setForm({ ...form, restaurantName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">優惠標題 *</label>
                  <input
                    value={form.title || ''}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="例如 全單 9 折"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">優惠詳情 / 細則</label>
                  <textarea
                    value={form.description || ''}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    支援 HTML 排版：&lt;strong&gt;粗體&lt;/strong&gt;、&lt;em&gt;斜體&lt;/em&gt;、&lt;br&gt;、&lt;p&gt;段落&lt;/p&gt;、&lt;ul&gt;&lt;li&gt;清單&lt;/li&gt;&lt;/ul&gt;、&lt;a href&gt;連結&lt;/a&gt;。WhatsApp 訊息會自動轉做 *粗體* _斜體_ 格式。
                  </p>
                  {form.description?.trim() && (
                    <div className="mt-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
                      <p className="text-xs font-bold text-gray-400 dark:text-gray-500 mb-1">顧客預覽</p>
                      <OfferDescription
                        text={form.description}
                        className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:font-bold [&_a]:text-red-600 [&_a]:underline"
                      />
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">優惠券圖片網址</label>
                  <div className="flex gap-2">
                    <input
                      value={form.imageUrl || ''}
                      onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                      placeholder="/media/media/offer-xxx.jpg 或完整 https URL"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowMediaPicker(true)}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center gap-1"
                    >
                      <Image size={16} /> 從媒體庫挑選
                    </button>
                  </div>
                  {form.imageUrl && (
                    <div className="mt-2 relative w-32 h-24 border rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                      <img src={form.imageUrl} alt="預覽" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-1">領取後經 WhatsApp 發送此圖（連兌換碼 caption）。</p>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">兌換說明（可選）</label>
                  <textarea
                    value={form.redeemInstructions || ''}
                    onChange={(e) => setForm({ ...form, redeemInstructions: e.target.value })}
                    rows={2}
                    placeholder="預設：出示此訊息同兌換碼畀店員，即可享用優惠。"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div className="sm:col-span-2 border border-purple-200 dark:border-purple-900/50 rounded-lg p-3 bg-purple-50/50 dark:bg-purple-900/10">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                    <ShieldCheck size={15} className="text-purple-600" /> 商家驗證碼（4 位數字，啟用 QR 自助核銷）
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      value={form.merchantVerifyCode || ''}
                      onChange={(e) => setForm({ ...form, merchantVerifyCode: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      placeholder="例如 1493"
                      className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white text-center font-mono text-lg tracking-widest"
                    />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, merchantVerifyCode: randomVerifyCode() })}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
                    >
                      隨機產生                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    建議用餐廳公司電話的最後 4 位。設定後，顧客兌換頁會顯示 QR Code，店員掃碼並輸入此碼即可核銷；未填時預設為 1493。
                  </p>
                  <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!form.allowReclaim}
                      onChange={(e) => setForm({ ...form, allowReclaim: e.target.checked ? 1 : 0 })}
                      className="w-4 h-4 text-teal-600 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                      <Repeat size={14} className="text-teal-600" /> 允許重複領取（兌換後可再領新兌換碼）
                    </span>
                  </label>
                  <p className="text-xs text-gray-400 mt-1">
                    開啟後，顧客兌換成功會收到「再傳送關鍵字即可再領」提示；每張新券照樣佔名額，舊記錄保留作歷史。
                  </p>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3 flex items-center gap-1">
                    <MessageCircle size={15} className="text-purple-600" /> 商家報告 WhatsApp（日報接收號碼）
                  </label>
                  <input
                    type="text"
                    inputMode="tel"
                    value={form.contactPhone || ''}
                    onChange={(e) => setForm({ ...form, contactPhone: e.target.value.replace(/\D/g, '') })}
                    placeholder="852xxxxxxxx（留空＝不發日報）"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    每朝早 9 點，若昨日此優惠有領取/核銷，系統會發一條摘要給這個號碼（週一必發）；同餐廳多個優惠會合併成一條。
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">有效期至（可選）</label>
                  <input
                    type="date"
                    value={validUntilDate}
                    onChange={(e) => setValidUntilDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">名額（可選，留空 = 不限）</label>
                  <input
                    type="number"
                    min={1}
                    value={form.maxClaims ?? ''}
                    onChange={(e) => setForm({ ...form, maxClaims: e.target.value ? Number(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序（細至大）</label>
                  <input
                    type="number"
                    value={form.sortOrder ?? 0}
                    onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <input
                    id="offer-active"
                    type="checkbox"
                    checked={!!form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked ? 1 : 0 })}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <label htmlFor="offer-active" className="ml-2 text-sm text-gray-700 dark:text-gray-300">上架</label>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">取消</button>
                <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center gap-2"><Save size={16} /> 儲存</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Media Picker */}
      {showMediaPicker && (
        <MediaPicker
          selectedUrl={form.imageUrl}
          onSelect={(url) => { setForm((prev) => ({ ...prev, imageUrl: url })); setShowMediaPicker(false); }}
          onClose={() => setShowMediaPicker(false)}
        />
      )}
    </AdminLayout>
  );
}
